import { Injectable } from "@angular/core";

import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { ServiceMessageBroker, ServiceMessageBrokerFactory } from "shared/provider-webworker";

import { OrderQueryRequestPromiseService } from "./OrderQueryRequestPromiseService";
import { CubiqWorker } from "../../worker/CubiqWorker";
import { FindOrdersDataOrderAdapter } from "../../mediator/adapter/FindOrdersDataOrderAdapter";
import { OrderDataPrivatizationService } from "../../model/privatization/OrderDataPrivatizationService";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { BenchmarkDataLookupService } from "../../lookup/model/impl/BenchmarkDataLookupService";
import { SiblingRelationshipManager } from "shared/cubiq-services/lookup/model/impl/SiblingRelationshipManager";
import { SysFeatures, SystemConfigPrimaryService } from "../../../auth-services";

export class QueryResponse {
  public isError: boolean = false;
  public found: Array<any> = null;
  public errors: CubiqTS.ErrorsImpl = null;
  public maxResultsReached: boolean = false;
}

const EX_INFO_MAX_QUERY_RESULT_REACHED: string = "max_total_results_reached";

@Injectable()
export class OrderQueryRequestPrimaryService extends OrderQueryRequestPromiseService {
  protected ready: Promise<void>;
  protected broker: ServiceMessageBroker;
  protected maxQueryResult: number = 1001;

  private _siblingRelationshipManager: SiblingRelationshipManager;

  constructor(
    private worker: CubiqWorker,
    protected factory: ServiceMessageBrokerFactory,
    private dataAdapter: FindOrdersDataOrderAdapter,
    private orderDataPrivatizationService: OrderDataPrivatizationService,
    private benchmarkDataLookupService: BenchmarkDataLookupService,
    private systemConfigService: SystemConfigPrimaryService,
  ) {
    super();

    this._siblingRelationshipManager = new SiblingRelationshipManager();
    this.ready = new Promise<void>((res, _rej) => {
      this.systemConfigService.getSystemConfig(SysFeatures.maxQueryResult).then((result) => {
        let maxQueryResult = Number.parseInt(result);

        if (maxQueryResult > 0) {
          // request one extra item to track if max exceeded
          this.maxQueryResult = maxQueryResult + 1;
        }
      });

      res();
    });
  }

  public request(orderQuery: CubiqTS.OrderQueryRequest): Promise<CubiqTS.StandardReport> {
    return new Promise<CubiqTS.StandardReport>((resolve, reject) => {
      Promise.all([this.worker.postServiceRequest(orderQuery), this.ready]).then(
        (values: any[]) => {
          let value = values[0];
          resolve(value);
        },
        (reason) => {
          reject(reason);
        },
      );
    });
  }

  public requestOrderData(orderQuery: CubiqTS.OrderQueryRequest): Promise<QueryResponse> {
    return new Promise<QueryResponse>((resolve, reject) => {
      Promise.all([this.worker.postServiceRequest(orderQuery), this.ready]).then(
        (values: any[]) => {
          logger.out.info("OrderQueryRequest: response received");
          let results: QueryResponse = new QueryResponse();

          if (Array.isArray(values)) {
            let value = values[0];
            if (value instanceof CubiqTS.ErrorsImpl) {
              results = this.handleFindOrdersErrors(orderQuery, value, results);
            } else {
              let report: CubiqTS.StandardReport = value as CubiqTS.StandardReport;

              results.found = report.results.map((orderData: CubiqTS.OrderDataImpl) => {
                orderData = this.orderDataPrivatizationService.checkPrivatization(orderData);
                if (orderData.gammaRungId) {
                  orderData.siblingRelationship =
                    this._siblingRelationshipManager.createDetachedRelationship();
                }

                // return this.dataAdapter.adaptRow(orderData);
                return orderData;
              });
              results.found = this.benchmarkDataLookupService.processExternalOrderDataForVisibility(
                results.found,
              );
              results.maxResultsReached =
                report.extendedInfo != null &&
                report.extendedInfo.get(EX_INFO_MAX_QUERY_RESULT_REACHED) === "Y";
            }
          }

          resolve(results);
        },
        (reason) => {
          reject(reason);
        },
      );
    });
  }

  handleFindOrdersErrors(
    query: CubiqTS.OrderQueryRequest,
    value: CubiqTS.ErrorsImpl,
    response: QueryResponse,
  ): QueryResponse {
    logger.out.error(
      `${this.constructor.name}: failed to find order based on critiera %O (%O)`,
      query,
      value,
    );

    response.errors = value;
    response.isError = true;

    return response;
  }

  handleFindTradesErrors(
    query: CubiqTS.TradeQueryRequest,
    value: CubiqTS.ErrorsImpl,
    response: QueryResponse,
  ): QueryResponse {
    logger.out.error(
      `${this.constructor.name}: failed to find trade based on critiera %O (%O)`,
      query,
      value,
    );

    response.errors = value;
    response.isError = true;

    return response;
  }
}
